'use client'
import { motion, useInView } from 'framer-motion'
import { useRef, useEffect, useState } from 'react'
import SectionTitle from "@/components/ui/SectionTitle";
import PostCard from "@/components/ui/PostCard";
import PostCardSkeleton from "@/components/ui/PostCardSkeleton";
import type { BlogPostCardViewModel } from "@/types/blog";
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext";
import { debugLog, debugWarn, logError } from "@/lib/utils/debug";

interface HomeBlogPostGridProps {
    title?: string;
    titleTag?: string;
    subTitle?: string | null;
    subTitleTag?: string | null;
    description?: string | null;
    descriptionTag?: string | null;
    /**
     * Blog post'ları (server-side fetch için)
     */
    posts?: BlogPostCardViewModel[];
    /**
     * Dil kodu (opsiyonel)
     */
    language?: string;
    languageCode?: string;
    languageId?: number;
    /**
     * Kaç post gösterilecek (default: 3, max: 20)
     */
    limit?: number;
    /**
     * Props'tan veri gelmezse client-side'da veri çekmeyi etkinleştir
     * GrapeJS preview için kullanışlıdır
     */
    enableClientFetch?: boolean;
    __pageContext?: {
        language?: string;
        languageCode?: string;
        languageId?: number;
    };
}

export default function HomeBlogPostGrid({ 
    title = "Updated News & Blogs", 
    titleTag = "h2",
    subTitle = "Explore the world", 
    subTitleTag = "span",
    description = null,
    descriptionTag = null,
    posts: initialPosts = [],
    language,
    languageCode,
    languageId,
    limit = 3,
    enableClientFetch = false,
    __pageContext,
}: HomeBlogPostGridProps) {
    const ref = useRef(null);
    const isInViewFromHook = useInView(ref, { once: true, margin: "-50px" });
    
    const [posts, setPosts] = useState<BlogPostCardViewModel[]>(initialPosts);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    
    // API'den veri geldiğinde isInView'ı true yap (animasyonların çalışması için)
    const [dataLoaded, setDataLoaded] = useState(false);
    
    // Language variables için state
    const [variables, setVariables] = useState<Record<string, string>>({});

    const resolvedLanguageCode =
        language ||
        languageCode ||
        __pageContext?.language ||
        __pageContext?.languageCode ||
        undefined;

    const resolvedLanguageId =
        languageId ||
        __pageContext?.languageId ||
        undefined;

    // Props'tan veri geldiyse client-side fetch yapma
    const hasInitialData = initialPosts.length > 0;
    const shouldFetch = enableClientFetch && !hasInitialData;
    
    // Eğer initialPosts varsa ve fetch yapılmayacaksa, dataLoaded'ı true yap
    const isInView = isInViewFromHook || dataLoaded;
    
    // Helper function to get variable with fallback
    const getVariable = (key: string, fallback: string): string => {
        return variables[key] || fallback;
    };
    

    // initialPosts'u ref ile sakla - sadece gerçekten değiştiğinde güncelle
    const prevInitialPostsLengthRef = useRef<number>(initialPosts.length);
    const prevInitialPostsIdsRef = useRef<string>(
        initialPosts.map(p => String(p.id || '')).sort().join(',')
    );

    // Language variables - Context'ten al (duplicate fetch'i önlemek için)
    const { variables: contextVariables } = useLanguageVariables();
    
    // Context'ten gelen variables'ı kullan, yoksa boş object
    useEffect(() => {
        if (contextVariables && Object.keys(contextVariables).length > 0) {
            setVariables(contextVariables);
        }
    }, [contextVariables]);

    // initialPosts değiştiğinde state'i güncelle (sadece shouldFetch false ise)
    useEffect(() => {
        if (!shouldFetch) {
            // Sadece gerçekten değiştiyse güncelle (length veya id'ler değişmişse)
            const currentIds = initialPosts.map(p => String(p.id || '')).sort().join(',');
            const hasChanged = 
                prevInitialPostsLengthRef.current !== initialPosts.length ||
                prevInitialPostsIdsRef.current !== currentIds;
            
            if (hasChanged) {
                setLoading(false);
                setError(null);
                setPosts(initialPosts);
                setDataLoaded(true); // Statik veri geldiğinde de animasyonların çalışması için
                prevInitialPostsLengthRef.current = initialPosts.length;
                prevInitialPostsIdsRef.current = currentIds;
            } else if (hasInitialData && !dataLoaded) {
                // İlk render'da initialPosts varsa, dataLoaded'ı true yap
                setDataLoaded(true);
            }
        } else if (hasInitialData && !dataLoaded) {
            // shouldFetch true olsa bile, eğer initialPosts varsa dataLoaded'ı true yap
            setDataLoaded(true);
        }
        // initialPosts'u dependency array'e ekle ama ref ile kontrol ettiğimiz için infinite loop olmaz
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldFetch, initialPosts, hasInitialData, dataLoaded]);

    // Client-side fetch için useEffect
    useEffect(() => {
        if (!shouldFetch) {
            return;
        }

        const abortController = new AbortController();
        const fetchPosts = async () => {
            try {
                setLoading(true);
                setError(null);

                // API parametrelerini oluştur
                const params = new URLSearchParams();
                
                // Dil parametresini belirle
                let finalLanguage: string | undefined = undefined;
                
                if (resolvedLanguageCode) {
                    finalLanguage = resolvedLanguageCode;
                } else if (resolvedLanguageId) {
                    params.set('language_id', String(resolvedLanguageId));
                } else if (typeof window !== 'undefined') {
                    // window.location'dan dil kodu çıkar
                    const pathParts = window.location.pathname.split('/').filter(Boolean);
                    const possibleLang = pathParts[0];
                    if (possibleLang && possibleLang.length === 2 && /^[a-z]{2}$/.test(possibleLang)) {
                        finalLanguage = possibleLang;
                    } else {
                        // Cookie'den dil al
                        const cookieLang = document.cookie
                            .split('; ')
                            .find(row => row.startsWith('language='))
                            ?.split('=')[1];
                        finalLanguage = cookieLang || undefined;
                    }
                }

                if (finalLanguage) {
                    params.set('language', finalLanguage);
                }

                const effectiveLimit = Math.min(Math.max(limit || 3, 1), 20);
                params.set('limit', String(effectiveLimit));

                const apiUrl = `/api/blog/recent?${params.toString()}`;

                const response = await fetch(apiUrl, {
                    signal: abortController.signal,
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    const errorMessage = errorData.message || errorData.error || `API error: ${response.status}`;
                    throw new Error(errorMessage);
                }

                const data = await response.json();
                
                // API response formatı: { success: true, data: BlogPostCardViewModel[] }
                let apiPosts: BlogPostCardViewModel[] = data?.data || [];
                
                // Image URL'leri JSON string ise parse et
                // Format: http://alsero.test:8082/storage/{"default":"media/...","small":"media/..."}
                apiPosts = apiPosts.map((post) => {
                    if (typeof post.src === 'string') {
                        // URL'de /storage/ sonrasında JSON string var mı kontrol et
                        const storageIndex = post.src.indexOf('/storage/');
                        if (storageIndex !== -1) {
                            const afterStorage = post.src.substring(storageIndex + '/storage/'.length);
                            // Eğer /storage/ sonrası { ile başlıyorsa JSON string var
                            if (afterStorage.startsWith('{')) {
                                try {
                                    const parsed = JSON.parse(afterStorage);
                                    // Eğer parsed bir obje ise, default veya small veya big field'ını kullan
                                    if (typeof parsed === 'object' && parsed !== null) {
                                        const imagePath = parsed.default || parsed.small || parsed.big || '';
                                        if (imagePath) {
                                            // Base URL'i al ve doğru path'i ekle
                                            const baseUrl = post.src.substring(0, storageIndex + '/storage/'.length);
                                            post.src = baseUrl + imagePath;
                                        }
                                    }
                                } catch (e) {
                                    debugWarn('[HomeBlogPostGrid] Failed to parse image URL:', post.src, e);
                                }
                            }
                        }
                    }
                    return post;
                });

                // State'i güncelle
                setPosts(apiPosts);
                setDataLoaded(true); // Veri yüklendiğinde animasyonların çalışması için
            } catch (err) {
                if (abortController.signal.aborted) {
                    return;
                }
                logError('Blog posts fetch error:', err);
                const errorMessage = err instanceof Error ? err.message : 'Blog yazıları yüklenemedi';
                setError(errorMessage);
                setPosts([]);
                setDataLoaded(true); // Hata durumunda da animasyonların çalışması için
            } finally {
                if (!abortController.signal.aborted) {
                    setLoading(false);
                }
            }
        };

        fetchPosts();
        return () => abortController.abort();
    }, [shouldFetch, resolvedLanguageCode, resolvedLanguageId, limit]);

    // shouldFetch true ise fetched posts kullan, değilse props'tan gelen veriyi kullan
    const allPosts = shouldFetch ? posts : initialPosts;
    const hasData = allPosts.length > 0;


    if (loading) {
        return (
            <motion.section
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{
                    duration: 0.6,
                    ease: "easeOut"
                }}
                className="w-full max-w-full sm:max-w-9/12 mx-auto mt-20 sm:mt-28 md:mt-[160px] mb-24 sm:mb-[70px] px-3 sm:px-4 relative min-w-0"
            >
                <SectionTitle 
                    title={title} 
                    titleTag={titleTag} 
                    subTitle={subTitle} 
                    subTitleTag={subTitleTag} 
                    description={description} 
                    descriptionTag={descriptionTag}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-16">
                    {Array.from({ length: limit || 3 }).map((_, index) => (
                        <PostCardSkeleton key={index} index={index} />
                    ))}
                </div>
            </motion.section>
        );
    }

    if (error) {
        return (
            <motion.section
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{
                    duration: 0.6,
                    ease: "easeOut"
                }}
                className="w-full max-w-full sm:max-w-9/12 mx-auto mt-20 sm:mt-28 md:mt-[160px] mb-24 sm:mb-[70px] px-3 sm:px-4 relative min-w-0"
            >
                <SectionTitle 
                    title={title} 
                    titleTag={titleTag} 
                    subTitle={subTitle} 
                    subTitleTag={subTitleTag} 
                    description={description} 
                    descriptionTag={descriptionTag}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-16 text-center text-red-500">
                    {getVariable('blog_posts_load_error', 'Blog posts could not be loaded')}
                </div>
            </motion.section>
        );
    }

    return (
        <motion.section
            className="w-full max-w-full sm:max-w-9/12 mx-auto mt-20 sm:mt-28 md:mt-[160px] mb-24 sm:mb-[70px] px-3 sm:px-4 relative min-w-0"
        >
            <SectionTitle 
                title={title} 
                titleTag={titleTag} 
                subTitle={subTitle} 
                subTitleTag={subTitleTag} 
                description={description} 
                descriptionTag={descriptionTag}
            />
            
            {!hasData && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-16 text-center text-alsero-light-gray">
                    {getVariable('blog_posts_no_posts', 'No blog posts found for this language yet.')}
                </div>
            )}

            {hasData && (
                <div ref={ref} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-16">
                    {allPosts.map((post, index) => (
                        <PostCard key={post.id || index} post={post} index={index} isInView={isInView} />
                    ))}
                </div>
            )}
        </motion.section>
    )
}

